import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { SelectableItem } from './SelectableItem';
import { BulkActionsMenu } from './BulkActionsMenu';
import type { PlotLine, Chapter } from '../types';

interface SelectionPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plotLines: PlotLine[];
  chapters: Chapter[];
  selectedTimelines: PlotLine[];
  selectedChapters: Chapter[];
  onTimelineSelect: (plotLine: PlotLine) => void;
  onChapterSelect: (chapter: Chapter) => void;
  onSelectAllTimelines: () => void;
  onSelectAllChapters: () => void;
  onClearTimelineSelection: () => void;
  onClearChapterSelection: () => void;
  onAddTimeline: () => void;
  onAddChapter: () => void;
  onReorderTimelines: () => void;
  onReorderChapters: () => void;
  onDeleteTimelines: () => void;
  onDeleteChapters: () => void;
}

export const SelectionPanel: React.FC<SelectionPanelProps> = ({
  open,
  onOpenChange,
  plotLines,
  chapters,
  selectedTimelines,
  selectedChapters,
  onTimelineSelect,
  onChapterSelect,
  onSelectAllTimelines,
  onSelectAllChapters,
  onClearTimelineSelection,
  onClearChapterSelection,
  onAddTimeline,
  onAddChapter,
  onReorderTimelines,
  onReorderChapters,
  onDeleteTimelines,
  onDeleteChapters
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Timeline Structure</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="timelines" className="mt-2">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="timelines">Timelines</TabsTrigger>
              <TabsTrigger value="chapters">Chapters</TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <TabsContent value="timelines" className="mt-0">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onAddTimeline}
                    className="h-8"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Timeline
                  </Button>

                  <BulkActionsMenu
                    type="timelines"
                    items={plotLines}
                    selectedItems={selectedTimelines}
                    onSelectAll={onSelectAllTimelines}
                    onClearSelection={onClearTimelineSelection}
                    onDelete={onDeleteTimelines}
                    onReorder={onReorderTimelines}
                  />
                </div>
              </TabsContent>

              <TabsContent value="chapters" className="mt-0">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onAddChapter}
                    className="h-8"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Chapter
                  </Button>

                  <BulkActionsMenu
                    type="chapters"
                    items={chapters}
                    selectedItems={selectedChapters}
                    onSelectAll={onSelectAllChapters}
                    onClearSelection={onClearChapterSelection}
                    onDelete={onDeleteChapters}
                    onReorder={onReorderChapters}
                  />
                </div>
              </TabsContent>
            </div>
          </div>

          <TabsContent value="timelines" className="mt-0">
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-1">
                {plotLines.length > 0 ? (
                  plotLines.map(timeline => (
                    <SelectableItem
                      key={timeline.id}
                      id={timeline.id}
                      name={timeline.name || timeline.title}
                      color={timeline.color || '#4299E1'}
                      isSelected={selectedTimelines.some(t => t.id === timeline.id)}
                      onSelect={(id) => {
                        const plotLine = plotLines.find(t => t.id === id);
                        if (plotLine) onTimelineSelect(plotLine);
                      }}
                    />
                  ))
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No timelines yet. Click "Add Timeline" to create one.
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="chapters" className="mt-0">
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-1">
                {chapters.length > 0 ? (
                  chapters.map(chapter => (
                    <SelectableItem
                      key={chapter.id}
                      id={chapter.id}
                      name={chapter.name || chapter.title}
                      isSelected={selectedChapters.some(c => c.id === chapter.id)}
                      onSelect={(id) => {
                        const chapter = chapters.find(c => c.id === id);
                        if (chapter) onChapterSelect(chapter);
                      }}
                    />
                  ))
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No chapters yet. Click "Add Chapter" to create one.
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
